/** Mirrors Valamar.Payment.Journey.Api.Models.PolicyManagement.AuditUserResponse. */
export interface AuditUserResponse {
  id: string;
  name: string;
}

/** Mirrors Valamar.Payment.Journey.Api.Models.PolicyManagement.PagedResponse<T>. */
export interface PagedResponse<T> {
  value: T[];
  nextLink?: string;
}

/** Query params accepted by GET /{family}-policies. Mirrors PolicyListQuery. */
export interface PolicyListQuery {
  search?: string;
  status?: 'draftOnly' | 'active' | 'inactive';
  hasDraft?: boolean;
  isAssigned?: boolean;
  orderby?: string;
  top?: number;
  skip?: number;
  maxpagesize?: number;
}

/** One row of the policy list. Mirrors PolicyListItemResponse. */
export interface PolicyListItemResponse {
  id: string;
  masterId: string;
  name: string;
  status: string;
  version?: number;
  hasDraft: boolean;
  assignmentCount: number;
  modifiedBy?: AuditUserResponse;
  modifiedAt?: string;
}

/** Fields shared by policy detail/version/draft representations. Mirrors PolicyDetailResponseBase. */
export interface PolicyDetailResponseBase extends PolicyListItemResponse {
  description?: string;
  createdBy?: AuditUserResponse;
  createdAt?: string;
  publishedBy?: AuditUserResponse;
  publishedAt?: string;
  baseVersion?: number;
}

/** Mirrors PaymentPolicyItemModel. */
export interface PaymentPolicyItemModel {
  name: string;
  description?: string;
  type: string;
  anchorDate: string;
  leadTimeRule?: string;
  chargeType: string;
  chargeAmount?: number;
  isRefundable?: boolean;
  offsetBaseDate?: string;
  offsetInHours?: number;
  triggerTime?: string;
  maxNumberOfRetries?: number;
  retryOffsetInHours?: number;
  validFrom?: string;
  validTo?: string;
  paymentTypeFilter?: string[];
}

/** Mirrors LocalizedTextModel. */
export interface LocalizedTextModel {
  lang: string;
  text: string;
}

/** Mirrors CancellationPolicyItemModel. */
export interface CancellationPolicyItemModel {
  offsetBaseDate: string;
  offsetFromInDays: number;
  offsetToInDays?: number;
  cutOffTime?: string;
  type?: string;
  value?: number;
  appliesTo?: string;
  validFrom?: string;
  validTo?: string;
  descriptions?: LocalizedTextModel[];
}

/** Mirrors ActivityPolicyItemModel. */
export interface ActivityPolicyItemModel {
  name: string;
  description?: string;
  type: string;
  anchorDate: string;
  leadTimeRule?: string;
  offsetBaseDate?: string;
  offsetInHours?: number;
  triggerTime?: string;
  isFinal?: boolean;
  validFrom?: string;
  validTo?: string;
  paymentTypeFilter?: string[];
}

/** Mirrors PaymentPolicyDetailResponse. */
export interface PaymentPolicyDetail extends PolicyDetailResponseBase {
  isVirtualCard?: boolean;
  items: PaymentPolicyItemModel[];
}

/** Mirrors CancellationPolicyDetailResponse. */
export interface CancellationPolicyDetail extends PolicyDetailResponseBase {
  items: CancellationPolicyItemModel[];
}

/** Mirrors ActivityPolicyDetailResponse. */
export interface ActivityPolicyDetail extends PolicyDetailResponseBase {
  items: ActivityPolicyItemModel[];
}

export type PolicyDetail = PaymentPolicyDetail | CancellationPolicyDetail | ActivityPolicyDetail;

export type PolicyFamily = 'payment' | 'cancellation' | 'activity';

export interface PolicyFamilyConfig {
  key: PolicyFamily;
  label: string;
  /** Literal backend route prefix, e.g. "payment-policies" — shared by Angular routes and API calls. */
  routeSegment: string;
}

export const POLICY_FAMILIES: PolicyFamilyConfig[] = [
  { key: 'payment', label: 'Payment', routeSegment: 'payment-policies' },
  { key: 'cancellation', label: 'Cancellation', routeSegment: 'cancellation-policies' },
  { key: 'activity', label: 'Activity', routeSegment: 'activity-policies' }
];

export interface PolicyItemColumn {
  key: string;
  label: string;
  /** How the generic items table should render this field's value. */
  type: 'text' | 'number' | 'boolean' | 'date' | 'array';
}

/**
 * Column definitions for the items table, hand-written from the *ItemModel shapes in policy.model.ts.
 * Stand-in for the future GET /policy-schemas/{family} endpoint (spec §8.5), which does not exist yet.
 * Replace this with a schema fetch once that endpoint ships — don't build a generic schema engine
 * against nothing real.
 */
export const POLICY_ITEM_COLUMNS: Record<PolicyFamily, PolicyItemColumn[]> = {
  payment: [
    { key: 'name', label: 'Name', type: 'text' },
    { key: 'type', label: 'Type', type: 'text' },
    { key: 'anchorDate', label: 'Anchor date', type: 'text' },
    { key: 'leadTimeRule', label: 'Lead time', type: 'text' },
    { key: 'chargeType', label: 'Charge type', type: 'text' },
    { key: 'chargeAmount', label: 'Amount', type: 'number' },
    { key: 'triggerTime', label: 'Trigger time', type: 'text' },
    { key: 'isRefundable', label: 'Refundable', type: 'boolean' },
    { key: 'validFrom', label: 'Valid from', type: 'date' },
    { key: 'validTo', label: 'Valid to', type: 'date' },
    { key: 'paymentTypeFilter', label: 'Payment types', type: 'array' }
  ],
  cancellation: [
    { key: 'offsetFromInDays', label: 'From (days)', type: 'number' },
    { key: 'offsetToInDays', label: 'To (days)', type: 'number' },
    { key: 'cutOffTime', label: 'Cut-off time', type: 'text' },
    { key: 'type', label: 'Type', type: 'text' },
    { key: 'value', label: 'Value', type: 'number' },
    { key: 'appliesTo', label: 'Applies to', type: 'text' },
    { key: 'validFrom', label: 'Valid from', type: 'date' },
    { key: 'validTo', label: 'Valid to', type: 'date' }
  ],
  activity: [
    { key: 'name', label: 'Name', type: 'text' },
    { key: 'type', label: 'Type', type: 'text' },
    { key: 'anchorDate', label: 'Anchor date', type: 'text' },
    { key: 'leadTimeRule', label: 'Lead time', type: 'text' },
    { key: 'triggerTime', label: 'Trigger time', type: 'text' },
    { key: 'isFinal', label: 'Final', type: 'boolean' },
    { key: 'validFrom', label: 'Valid from', type: 'date' },
    { key: 'validTo', label: 'Valid to', type: 'date' }
  ]
};
